import { useEffect, useState } from 'react';
import { Alert, Button, Card, Container, Form, Spinner } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { useDashboardTopList } from '../../apps/apis/claudeCodeDashboard/hooks';
import type {
  DashboardTopListItem,
  DashboardTopListKind,
  TokenUsageByEmployeeRow,
  TopAgentRow,
  TopMcpToolRow,
  TopSkillRow,
} from '../../apps/apis/claudeCodeDashboard/types';
import ClaudeCodeLoginGate from '../../components/claude-code/ClaudeCodeLoginGate';
import EmployeeTokenRanking from '../../components/claude-code/EmployeeTokenRanking';
import TopAgentsTable from '../../components/claude-code/TopAgentsTable';
import TopMcpToolsTable from '../../components/claude-code/TopMcpToolsTable';
import TopSkillsTable from '../../components/claude-code/TopSkillsTable';
import { HELP_TEXT } from '../../components/claude-code/agentTelemetryHelp';
import { HelpLabel } from '../../components/claude-code/HelpTooltip';
import { ROUTES } from '../../routes/routes';

const WINDOW_OPTIONS = [7, 14, 30, 60, 90];
const PAGE_SIZE = 50;

const TOP_LIST_LABELS: Record<DashboardTopListKind, string> = {
  skills: 'Top skills',
  agents: 'Top agents',
  'mcp-tools': 'Top MCP tools',
  employees: 'Token usage by employee',
};

const TOP_LIST_HELP: Record<DashboardTopListKind, string> = {
  skills: HELP_TEXT.topSkills,
  agents: HELP_TEXT.topAgents,
  'mcp-tools': HELP_TEXT.topMcpTools,
  employees: HELP_TEXT.tokenUsageByEmployee,
};

const VALID_KINDS = Object.keys(TOP_LIST_LABELS) as DashboardTopListKind[];

const isDashboardTopListKind = (kind: string | undefined): kind is DashboardTopListKind =>
  kind !== undefined && VALID_KINDS.includes(kind as DashboardTopListKind);

const asTopSkillRows = (items: DashboardTopListItem[]): TopSkillRow[] => items as TopSkillRow[];
const asTopAgentRows = (items: DashboardTopListItem[]): TopAgentRow[] => items as TopAgentRow[];
const asTopMcpToolRows = (items: DashboardTopListItem[]): TopMcpToolRow[] => items as TopMcpToolRow[];
const asEmployeeRows = (items: DashboardTopListItem[]): TokenUsageByEmployeeRow[] => items as TokenUsageByEmployeeRow[];

const AgentTelemetryTopListPage = () => {
  const params = useParams();
  const kind = params['kind'];

  if (!isDashboardTopListKind(kind)) {
    return (
      <Container fluid className="py-4">
        <Alert variant="warning">
          Unsupported top list. <Link to={ROUTES.agentTelemetry.overview}>Back to overview</Link>
        </Alert>
      </Container>
    );
  }

  return (
    <ClaudeCodeLoginGate>
      {(session) => (
        <Container fluid className="py-4">
          <div className="d-flex align-items-end justify-content-between mb-3 flex-wrap gap-2">
            <div>
              <h2 className="mb-1">
                <HelpLabel description={TOP_LIST_HELP[kind]}>Agent Telemetry — {TOP_LIST_LABELS[kind]}</HelpLabel>
              </h2>
              <div className="text-muted small">
                Signed in as <strong>{session.email}</strong>
                {session.isAdmin ? <span className="ms-2 badge bg-primary">admin</span> : null}
              </div>
            </div>
            <Link to={ROUTES.agentTelemetry.overview} className="btn btn-outline-secondary btn-sm">
              Back to overview
            </Link>
          </div>
          {session.isAdmin ? <TopListBody kind={kind} /> : <Alert variant="warning">Top lists are admin-only.</Alert>}
        </Container>
      )}
    </ClaudeCodeLoginGate>
  );
};

const TopListBody = ({ kind }: { kind: DashboardTopListKind }) => {
  const [windowDays, setWindowDays] = useState(30);
  const [offset, setOffset] = useState(0);
  const { data, isLoading, error } = useDashboardTopList(kind, windowDays, PAGE_SIZE, offset);

  useEffect(() => {
    if (!data) {
      return;
    }
    const maxOffset = data.total > 0 ? Math.floor((data.total - 1) / PAGE_SIZE) * PAGE_SIZE : 0;
    if (offset > maxOffset) {
      setOffset(maxOffset);
    }
  }, [data, offset]);

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const canGoBack = offset > 0;
  const canGoForward = data ? offset + data.limit < data.total : false;
  const visibleStart = data && data.total > 0 ? offset + 1 : 0;
  const visibleEnd = data ? Math.min(offset + data.limit, data.total) : 0;

  return (
    <Card>
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <Card.Title className="h6 mb-1">
              <HelpLabel description={TOP_LIST_HELP[kind]}>{TOP_LIST_LABELS[kind]}</HelpLabel>
            </Card.Title>
            <div className="text-muted small">
              {data ? `${data.total.toLocaleString()} rows` : 'Loading rows'} · page {page}
            </div>
          </div>
          <Form.Group controlId="topListWindowDays" className="d-flex align-items-center gap-2">
            <Form.Label className="mb-0 small text-muted">
              <HelpLabel description={HELP_TEXT.window}>Window</HelpLabel>
            </Form.Label>
            <Form.Select
              size="sm"
              value={windowDays}
              onChange={(event) => {
                setWindowDays(Number(event.target.value));
                setOffset(0);
              }}
              style={{ width: 120 }}
            >
              {WINDOW_OPTIONS.map((days) => (
                <option key={days} value={days}>
                  {days} days
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>

        {isLoading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" />
          </div>
        ) : error ? (
          <Alert variant="danger">Failed to load top list: {(error as Error).message}</Alert>
        ) : data ? (
          <>
            {kind === 'skills' ? <TopSkillsTable data={asTopSkillRows(data.items)} /> : null}
            {kind === 'agents' ? <TopAgentsTable data={asTopAgentRows(data.items)} /> : null}
            {kind === 'mcp-tools' ? <TopMcpToolsTable data={asTopMcpToolRows(data.items)} /> : null}
            {kind === 'employees' ? <EmployeeTokenRanking data={asEmployeeRows(data.items)} /> : null}
            <div className="d-flex justify-content-end align-items-center gap-2 mt-3">
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={!canGoBack}
                onClick={() => setOffset(Math.max(offset - PAGE_SIZE, 0))}
              >
                Previous
              </Button>
              <span className="text-muted small">
                {visibleStart}-{visibleEnd} of {data.total}
              </span>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={!canGoForward}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          </>
        ) : null}
      </Card.Body>
    </Card>
  );
};

export default AgentTelemetryTopListPage;
